import { Flag, LoadingMessage } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { FormProvider, useForm } from 'react-hook-form';
import { Outlet, useLocation } from 'react-router-dom';
import { CspHeader } from '../components/navigation/cspHeader/cspHeader';
import { BrandContext } from '../contextProviders/brandContext';
import { CSPUrlsMap } from '../routes/CSPUrlsMap';
import '../styles/themes/base/base.scss';
import { SESSION_STORAGE_KEYS } from '../constants/sessionStorageKeys';
import { SLICES } from '../constants/slices';
import { useGetWelcomeDashboardQuery } from '../data/customer';
import { useGetAdjustmentPolicyContentQuery } from '../data/kontentAi';
import { getPolicyHolderName } from '../helpers/policyHelpers';
import { getPetNames } from '../helpers/petHelpers';
import CspFooter from '../components/navigation/cspFooter/cspFooter';
import CspDisclaimer from '../components/disclaimer/cspDisclaimer';
import WelcomeMessage from '../components/welcomeMessage/welcomeMessage';
import { scrollToTop } from '../utils/scrollUtils';
import { AUTHENTICATION_TYPES } from '../constants/constants';
import RenewalBanner from '../components/renewal/banner/renewalBanner';
import { setTermsOfUsePopupVisible } from '../store/slice/dashboardSlice';
import { FeatureFlagContext } from '../contextProviders/featureFlagContext';
import { useResponsiveConfig } from '../hooks/responsiveConfigSwap';
import { BREAK_POINT } from '../components/renewal/consts';
import { MobileAdjustedRenewalSummarySticky } from '../components/renewal/summary/mobileAdjustedRenewalSummarySticky';
import { RENEWAL_HUB_CONTENT } from '../constants/cmsCodeName';

export const DefaultLayout = ({ isLoading = false }) => {
  const formContext = useForm({
    mode: 'all',
    criteriaMode: 'all',
    reValidateMode: 'onChange'
  });

  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isLiteModeEnabled = cspConfiguration?.IsLiteModeEnabled;
  const skipQueryOnLiteMode = isLiteModeEnabled !== false;
  const { petDetailsList } = useSelector((state) => state?.[SLICES.PET_DETAILS]);
  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { renewalSummary, adjustedSummary } = useSelector((state) => state?.[SLICES.RENEWAL_HUB] || {});
  const { isMobile } = useResponsiveConfig({
    configDefinition: { default: { isMobile: false }, [BREAK_POINT]: { isMobile: true } }
  });
  const { data: firstLoginCheck, isLoading: isLoadingGetWelcomeDashboardQuery } = useGetWelcomeDashboardQuery(
    {
      auth0Token: authDetails.auth0AccessToken,
      isFallback: cspConfiguration?.IsFallbackModeMiddlewareApiEnabled,
      contactId: authDetails.contactId
    },
    { skip: !authDetails.auth0AccessToken || !cspConfiguration || !authDetails.contactId || skipQueryOnLiteMode }
  );
  const hideHeaderFooter =
    pathname === CSPUrlsMap.postConsultation.url ||
    pathname === CSPUrlsMap.verification.url ||
    pathname === CSPUrlsMap.login.url ||
    pathname === CSPUrlsMap.logout.url;
  const {
    showBrandSwitch,
    activeBrandContext: {
      brand: { key: activeBrand, logos },
      layout: { bodyCssClass },
      cspConfiguration: {
        partnerTitle,
        bannerImage,
        termsOfUseUrl,
        footerLogo,
        auth0Config: {
          [process.env.REACT_APP_ENV]: { authenticationType }
        }
      }
    }
  } = useContext(BrandContext);

  const isFirstLogin = sessionStorage.getItem(SESSION_STORAGE_KEYS.IS_FIRST_LOGIN) === 'true';
  const isSSOAuthentication = authenticationType === AUTHENTICATION_TYPES.SSO;
  const isWelcomeDashboard =
    ((isSSOAuthentication
      ? firstLoginCheck?.TermsOfUseStatus == null && firstLoginCheck?.TakeATourStatus == null
      : firstLoginCheck?.TakeATourStatus == null) &&
      !isLoadingGetWelcomeDashboardQuery) ||
    isFirstLogin ||
    false;
  const { termsOfUsePopupVisible } = useSelector((state) => state?.[SLICES.DASHBOARD]);
  const environment = process.env.REACT_APP_ENV;
  const bannerUrl = environment !== 'prod' ? bannerImage.nonprod : bannerImage.prod;
  const hasBannerImage = bannerUrl !== '';
  const showWelcomeMessage = pathname === CSPUrlsMap.dashboard.url && !isLoadingGetWelcomeDashboardQuery;
  const showRenewalBanners =
    pathname === CSPUrlsMap.dashboard.url &&
    !cspConfiguration?.IsLiteModeEnabled &&
    cspConfiguration?.IsRenewalHubEnabled;
  const isRenewalHubPath = pathname === CSPUrlsMap.renewalHub.url;
  const isKontentAiPreviewEnabled = cspConfiguration?.IsKontentAIPreviewEnabled || false;
  const { data: adjustmentPolicyContent, isLoading: isAdjustmentPolicyContentLoading } =
    useGetAdjustmentPolicyContentQuery(
      {
        previewEnabled: isKontentAiPreviewEnabled,
        element: [
          RENEWAL_HUB_CONTENT.INTRODUCTION_CONTENT,
          RENEWAL_HUB_CONTENT.ADJUST_COVER,
          RENEWAL_HUB_CONTENT.OPTIONAL_EXTRAS,
          RENEWAL_HUB_CONTENT.CHANGE_SUMMARY,
          RENEWAL_HUB_CONTENT.PANEL_SUMMARY
        ]
      },
      { skip: !isRenewalHubPath || !authDetails.auth0AccessToken }
    );
  const shouldShowMobileRenewalStickySummary =
    pathname === CSPUrlsMap.renewalHub.url &&
    isMobile &&
    !!renewalSummary &&
    !!adjustedSummary?.isCoverAdjusted &&
    !hideHeaderFooter &&
    !termsOfUsePopupVisible;
  const pageTitle = useMemo(() => {
    const defaultPageTitle = `${partnerTitle}`;
    if (pathname === '/') {
      return defaultPageTitle;
    }
    const normalizedPathname = pathname.replace(/\/+$/, '');
    const routeForPath = Object.values(CSPUrlsMap).find(({ url }) => url.replace(/\/+$/, '') === normalizedPathname);

    if (!routeForPath?.title) {
      return defaultPageTitle;
    }

    return `${defaultPageTitle} - ${routeForPath.title}`;
  }, [pathname, partnerTitle]);

  useEffect(() => {
    if (!isLoadingGetWelcomeDashboardQuery) {
      if (isLiteModeEnabled) {
        dispatch(setTermsOfUsePopupVisible(false));
        return;
      }

      if (firstLoginCheck === undefined) {
        return;
      }

      const showTermsOfUseFlag = firstLoginCheck?.TermsOfUseStatus == null && isSSOAuthentication;
      dispatch(setTermsOfUsePopupVisible(showTermsOfUseFlag));
    }
  }, [isLoadingGetWelcomeDashboardQuery, firstLoginCheck, isSSOAuthentication, dispatch, isLiteModeEnabled]);

  useEffect(() => {
    scrollToTop();
  }, [pathname]);

  if (isLoading) {
    return <LoadingMessage />;
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <body className={`react petSure customerServicePortal ${bodyCssClass} ${activeBrand}`} />
      </Helmet>

      <FormProvider {...formContext}>
        <div className="petSureForm">
          {environment !== 'prod' && (
            <Flag
              customCssClass="environmentFlag"
              label={environment}
            />
          )}

          {!hideHeaderFooter && (
            <div
              className={`pageImageBanner ${!hasBannerImage ? 'noBannerImage' : ''}`}
              style={{
                backgroundImage: hasBannerImage ? `url(${bannerUrl})` : 'none'
              }}
            >
              <CspHeader />
              {showWelcomeMessage && petDetailsList.length > 0 && (
                <WelcomeMessage
                  isWelcomeDashboard={isWelcomeDashboard}
                  policyHolderName={getPolicyHolderName(petDetailsList)}
                  petName={getPetNames(petDetailsList)}
                />
              )}
            </div>
          )}

          {showRenewalBanners && (
            <div className="renewalBanners">
              <RenewalBanner
                type="full"
                location="dashboard"
              />
            </div>
          )}

          <main
            className={`cspPageContainer ${!hasBannerImage ? 'noBannerImageContainer' : ''} ${
              shouldShowMobileRenewalStickySummary ? 'renewalPageWithMobileStickySummary' : ''
            }`}
            aria-label="Main App Content"
          >
            <Outlet
              context={{
                adjustmentPolicyContent,
                isAdjustmentPolicyContentLoading
              }}
            />
          </main>
          {!hideHeaderFooter && !termsOfUsePopupVisible && (
            <>
              <CspDisclaimer />
              <CspFooter
                termsUrlByBrand={termsOfUseUrl}
                logo={environment !== 'prod' ? footerLogo.nonprod : footerLogo.prod}
              />
            </>
          )}
          <MobileAdjustedRenewalSummarySticky
            isVisible={shouldShowMobileRenewalStickySummary}
            renewalSummary={renewalSummary}
            cmsData={adjustmentPolicyContent?.content}
            isLoading={isAdjustmentPolicyContentLoading}
          />
        </div>
      </FormProvider>
    </>
  );
};

DefaultLayout.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  user: PropTypes.any,
  // eslint-disable-next-line react/forbid-prop-types
  children: PropTypes.any,
  isLoading: PropTypes.bool,
  headerShouldPin: PropTypes.bool,
  headerPageTitleText: PropTypes.string,
  logoUrl: PropTypes.string,
  forceShowNavLinks: PropTypes.bool,
  bodyCssClass: PropTypes.string
};

DefaultLayout.defaultProps = {
  children: null
};
